import { BlockTypeMenu } from '@/components/admin/experiences/page-editor/block-type-menu';
import { PAGE_EDITOR_IDS } from '@/components/admin/experiences/page-editor/contracts';
import type { BlockType } from '@matter_extractor/shared';
import { EXPERIENCE_TEMPS_MAX } from '@matter_extractor/shared';
import { Icon } from '@pms-digital/design-system';

/**
 * « Ajouter » - ce qu'il en RESTE : un temps (refonte modulaire §9.3; planche
 * `AjouterUnBloc`; issues #58 puis #136).
 *
 * ## Pourquoi les neuf boutons de type sont partis
 *
 * Ils posaient le bloc neuf « à la fin du temps du bloc sélectionné, sinon du
 * dernier », ce qui oblige à sélectionner avant d'ajouter et, quand on oublie,
 * à glisser la ligne sur sept rangs. Le geste vit désormais à l'endroit où le
 * bloc atterrit : le « + » de chaque séparateur de temps, qui ouvre la même
 * planche de neuf types (`block-type-menu.tsx`, popover). Rien n'est perdu -
 * mêmes icônes, mêmes libellés, mêmes bulles - et l'adresse cesse d'être
 * implicite.
 *
 * Ce qui reste ici est ce que le séparateur ne peut pas offrir : ajouter un
 * TEMPS, qui n'appartient à aucun temps existant. Le cadre en tirets n'est plus
 * là pour distinguer ce bouton de neuf autres, mais il reste : c'est la marque
 * d'un geste qui crée un moment plutôt qu'un contenu, et la même qu'ailleurs.
 *
 * ## La page sans aucun temps a son propre « + »
 *
 * Une page peut n'avoir aucun temps - la page protocole du modèle naît
 * `temps: []`, et `addBlock` en sème un au premier bloc. Cette page-là n'a donc
 * aucun séparateur pour porter un « + », et la planche des types n'aurait
 * nulle part où s'ouvrir. Elle montre « Ajouter un premier bloc », le même
 * popover, déclencheur nommé : un « + » seul, sur une colonne vide, serait un
 * geste à deviner.
 */

export interface AddBlockGridProps {
  readonly readOnly: boolean;
  readonly tempsCount: number;
  /** Le premier bloc d'une page vide - il sème son temps au passage (§9.3). */
  readonly onAddFirstBlock: (type: BlockType) => void;
  readonly onAddTemps: () => void;
}

export function AddBlockGrid({
  readOnly,
  tempsCount,
  onAddFirstBlock,
  onAddTemps,
}: AddBlockGridProps) {
  const tempsFull = tempsCount >= EXPERIENCE_TEMPS_MAX;
  return (
    <section data-testid={PAGE_EDITOR_IDS.addGrid} className="space-y-2">
      <h2 className="font-semibold text-bleu-nuit text-small">Ajouter</h2>

      {tempsCount === 0 ? (
        <BlockTypeMenu
          label="Ajouter un premier bloc"
          trigger="named"
          disabled={readOnly}
          onPick={onAddFirstBlock}
        />
      ) : null}

      <button
        type="button"
        data-testid={PAGE_EDITOR_IDS.addTemps}
        disabled={readOnly || tempsFull}
        onClick={onAddTemps}
        className="flex w-full cursor-pointer items-center justify-center gap-2 rounded-md border-2 border-gray-300 border-dashed bg-white px-3 py-2 font-semibold text-gray-700 text-caption transition-colors hover:border-bleu-nuit hover:text-bleu-nuit focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-bleu-nuit disabled:cursor-not-allowed disabled:opacity-60"
      >
        <Icon name="add" size={14} />
        Ajouter un temps
      </button>

      <p className="text-gray-600 text-caption leading-4">
        {tempsFull
          ? `Une page porte au plus ${EXPERIENCE_TEMPS_MAX} temps.`
          : 'Un bloc s’ajoute par le « + » du temps qui doit le montrer ; un temps neuf se place après le dernier. Glisse un bloc pour le réordonner ou le changer de temps.'}
      </p>
    </section>
  );
}
